import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  RadioButtonUnchecked, 
  CloudUpload, 
  Chat, 
  Event, 
  AccountCircle,
  TrendingUp,
  Assignment
} from '@mui/icons-material';

const DashboardWrapper = styled.div`
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  
  h1 {
    color: ${props => props.theme.colors.darkGreen};
    font-size: 2.5rem;
  }
`;

const WelcomeCard = styled.div`
  background: ${props => props.theme.colors.darkGreen};
  color: white;
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusSection = styled.div`
  background: ${props => props.theme.colors.background};
  padding: 2rem;
  border-radius: 15px;
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 2rem;
`;

const StatusTitle = styled.h3`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProgressTrack = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin-bottom: 3rem;

  &:before {
    content: '';
    position: absolute;
    top: 15px;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.border};
    z-index: 1;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  z-index: 2;
  background: ${props => props.theme.colors.background};
  padding: 0 10px;

  svg {
    color: ${props => props.active ? props.theme.colors.darkGreen : props.theme.colors.gray};
    background: ${props => props.theme.colors.background};
  }

  span {
    font-size: 0.8rem;
    font-weight: ${props => props.active ? 'bold' : 'normal'};
    color: ${props => props.active ? props.theme.colors.darkGreen : props.theme.colors.gray};
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.background};
  padding: 1.5rem;
  border-radius: 15px;
  border: 1px solid ${props => props.theme.colors.border};
  height: 100%;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${props => props.theme.colors.lightGray};
  border-radius: 8px;

  .info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`;

const UploadButton = styled.button`
  background: ${props => props.theme.colors.darkGreen};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const Dashboard = () => {
  const documents = [
    { id: 1, name: 'תלושי שכר (3 אחרונים)', status: 'completed' },
    { id: 2, name: 'תעודת זהות + ספח', status: 'pending' },
    { id: 3, name: 'עובר ושב (3 חודשים)', status: 'pending' },
    { id: 4, name: 'אישור עקרוני מהבנק', status: 'missing' }
  ];

  const steps = [
    { id: 1, label: 'פגישת ייעוץ', active: true },
    { id: 2, label: 'איסוף מסמכים', active: true },
    { id: 3, label: 'מכרז בנקים', active: false },
    { id: 4, label: 'חתימות', active: false },
    { id: 5, label: 'סיום', active: false }
  ];

  return (
    <DashboardWrapper>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <DashboardHeader>
          <h1>פורטל לקוחות</h1>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Chat style={{ cursor: 'pointer' }} />
            <Event style={{ cursor: 'pointer' }} />
          </div>
        </DashboardHeader>

        <WelcomeCard>
          <div>
            <h2>שלום, ישראל ישראלי</h2>
            <p>התיק שלך נמצא כרגע בשלב "איסוף מסמכים". אנחנו מחכים למספר מסמכים כדי להתקדם למכרז הבנקים.</p>
          </div>
          <AccountCircle style={{ fontSize: '4rem', opacity: 0.5 }} />
        </WelcomeCard>

        <StatusSection>
          <StatusTitle>
            <TrendingUp /> סטטוס הבקשה
          </StatusTitle>
          <ProgressTrack>
            {steps.map(step => (
              <ProgressStep key={step.id} active={step.active}>
                {step.active ? <CheckCircle /> : <RadioButtonUnchecked />}
                <span>{step.label}</span>
              </ProgressStep>
            ))}
          </ProgressTrack>
        </StatusSection>

        <DashboardGrid>
          <Card>
            <StatusTitle>
              <Assignment /> רשימת מסמכים נדרשים
            </StatusTitle>
            <DocumentList>
              {documents.map(doc => (
                <DocumentItem key={doc.id}>
                  <div className="info">
                    {doc.status === 'completed' ? (
                      <CheckCircle style={{ color: '#4caf50' }} />
                    ) : (
                      <RadioButtonUnchecked style={{ color: doc.status === 'missing' ? '#ff4444' : '#666' }} />
                    )}
                    <span>{doc.name}</span>
                  </div>
                  {doc.status !== 'completed' && (
                    <UploadButton>
                      <CloudUpload fontSize="small" /> העלאה
                    </UploadButton>
                  )}
                </DocumentItem>
              ))}
            </DocumentList>
          </Card>

          <Card>
            <StatusTitle>היועץ שלך</StatusTitle>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: '#eee', 
                margin: '0 auto 1rem',
                backgroundImage: 'url(/images/afik-profile.png)',
                backgroundSize: 'cover'
              }} />
              <h4>אפיק לב</h4>
              <p style={{ fontSize: '0.9rem', color: '#666' }}>יועצת משכנתאות בכירה</p>
              <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <UploadButton style={{ width: '100%', justifyContent: 'center' }}>
                  <Chat /> צ'אט עם אפיק
                </UploadButton>
                <UploadButton style={{ 
                  width: '100%', 
                  justifyContent: 'center', 
                  background: 'transparent', 
                  color: '#2F4F4F',
                  border: '1px solid #2F4F4F'
                }}>
                  תיאום פגישה
                </UploadButton>
              </div>
            </div>
          </Card>
        </DashboardGrid>
      </motion.div>
    </DashboardWrapper>
  );
};

export default Dashboard;
